// Resolves a DataDisplaySpec + optional fallback categories into a ResolvedDataDisplay.
import type { DataDisplaySpec, AggField, AggFn } from '../types';
import type { ResolvedDataDisplay } from './datadisplay.types';
import { defaultDisplayFields } from './datadisplay.defaults';
import { toolDataDisplayOverrides } from './datadisplay.tool-overrides';
import { toArray } from '../types';
import type { SupportedTools } from '../../../interfaces';

/**
 * Merge field lists from multiple categories, preserving order and deduplicating.
 * Pulls from `set` ('summary' | 'detail') in defaultDisplayFields.
 */
function categoryFields(categories: string[], set: 'summary' | 'detail'): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const cat of categories) {
    const entry = defaultDisplayFields[cat];
    if (!entry) continue;
    for (const f of entry[set]) {
      if (!seen.has(f)) {
        seen.add(f);
        result.push(f);
      }
    }
  }
  return result;
}

/** Deduplicate a string array while preserving order. */
function dedup(arr: string[]): string[] {
  const seen = new Set<string>();
  return arr.filter((f) => (seen.has(f) ? false : (seen.add(f), true)));
}

/**
 * Resolve a DataDisplaySpec + fallback categories into a normalized ResolvedDataDisplay.
 *
 * Field resolution order (projection fields):
 *  1. spec.fields (explicit override — highest priority, bypasses all tool overrides)
 *  2. activeView='summary' → spec.summaryFields ?? global defaults + tool override summary (additive)
 *  3. activeView='detail' (default) → spec.detailFields ?? global defaults + tool override detail (additive)
 *  4. activeView='custom' → [] (aggFields-only; no projection)
 *
 * Aggregation resolution order:
 *  1. spec.groupBy / spec.aggFields (explicit — highest priority)
 *  2. Tool override aggFields (per-category replacement; sets groupBy additively from identityFields)
 *  3. Auto-derive from identityFields (groupBy) + remaining detail fields (aggFields with fn:'values')
 *
 * In project mode (displayMode='project'), groupBy is never set — projection fields are emitted instead.
 * Identity fields are reordered to the front of the projection for project mode.
 *
 * Categories: spec.category > fallbackCategories. Multiple categories merge + deduplicate.
 */
export function resolveDataDisplay(
  spec: DataDisplaySpec,
  fallbackCategories: string[] | undefined,
  tool?: SupportedTools,
): ResolvedDataDisplay {
  // Determine effective categories
  const specCats = toArray(spec.category);
  const cats = specCats.length ? specCats : (fallbackCategories ?? []);

  // Resolve field list
  let fields: string[];
  if (spec.fields?.length) {
    fields = dedup(spec.fields);
  } else {
    const view = spec.activeView ?? 'detail';
    if (view === 'custom') {
      fields = [];
    } else if (view === 'summary') {
      fields = spec.summaryFields?.length
        ? dedup(spec.summaryFields)
        : categoryFields(cats, 'summary');
    } else {
      // 'detail' (default)
      fields = spec.detailFields?.length
        ? dedup(spec.detailFields)
        : categoryFields(cats, 'detail');
    }
  }

  // Apply per-tool projection field overrides (additive).
  // Tool override summary/detail lists are appended after global defaults; dedup preserves order.
  // Fields must have CCM mappings — unmapped fields are filtered out by fieldExists at render time.
  // Categories without a tool override fall back to defaultDisplayFields only.
  if (!spec.fields?.length && tool) {
    const toolOverrides = toolDataDisplayOverrides[tool]
    if (toolOverrides) {
      const view = spec.activeView ?? 'detail'
      if (view !== 'custom') {
        const merged: string[] = []
        const seen = new Set<string>()
        for (const cat of cats) {
          const catOverride = toolOverrides[cat]
          const defaults = defaultDisplayFields[cat]
          const src = [...(defaults?.[view] ?? []), ...(catOverride?.[view] ?? [])]
          for (const f of src) {
            if (!seen.has(f)) { seen.add(f); merged.push(f) }
          }
        }
        fields = merged
      }
    }
  }

  const mode = spec.displayMode ?? 'aggregate';

  // In project mode, reorder so identity fields appear first, then remaining fields.
  // Uses global defaults only for identity ordering — tool override identityFields are additive
  // to groupBy (aggregation), not used for projection ordering.
  if (mode === 'project' && fields.length > 0) {
    const identityFields = dedup(cats.flatMap((c) => defaultDisplayFields[c]?.identityFields ?? []))
    const identitySet = new Set(identityFields)
    const identityInFields = identityFields.filter((f) => fields.includes(f))
    const rest = fields.filter((f) => !identitySet.has(f))
    fields = dedup([...identityInFields, ...rest])
  }

  let groupBy = dedup(spec.groupBy ?? []);
  const rawAggFields = spec.aggFields ?? [];
  let aggFields: AggField[] = rawAggFields.map((af) =>
    typeof af === 'string' ? { field: af, fn: 'values' as AggFn } : af,
  );

  // Apply tool-level aggFields override (per-category explicit agg spec).
  // When present it bypasses auto-derivation entirely — the override fully controls
  // which fields go in the groupBy clause and which functions are applied to each field.
  // Only fires when the spec itself has no explicit aggFields or groupBy, and mode is not 'project'
  // (project mode must never set groupBy — it renders a field projection, not a stats clause).
  if (!spec.aggFields?.length && !spec.groupBy?.length && tool && mode !== 'project') {
    const toolOverrides = toolDataDisplayOverrides[tool]
    if (toolOverrides) {
      for (const cat of cats) {
        const catOverride = toolOverrides[cat]
        if (catOverride?.aggFields?.length) {
          aggFields = catOverride.aggFields.map((af) => ({ ...af, fromOverride: true }))
          // Derive groupBy: defaults first, then any additive tool-specific identity fields
          groupBy = dedup(
            cats.flatMap((c) => [
              ...(defaultDisplayFields[c]?.identityFields ?? []),
              ...(toolOverrides[c]?.identityFields ?? []),
            ]),
          )
          break
        }
      }
    }
  }

  // Auto-derive groupBy + aggFields from identityFields when in aggregate mode,
  // no explicit groupBy/aggFields were provided, and no tool aggFields override fired above.
  // groupBy = global identityFields + tool identityFields (additive).
  // aggFields = remaining detail fields, each with fn:'values' (no fromOverride — must be CCM-mapped).
  if (mode === 'aggregate' && groupBy.length === 0 && aggFields.length === 0 && fields.length > 0) {
    const toolOverrides = tool ? toolDataDisplayOverrides[tool] : undefined
    groupBy = dedup(cats.flatMap((c) => [
      ...(defaultDisplayFields[c]?.identityFields ?? []),
      ...(toolOverrides?.[c]?.identityFields ?? []),
    ]));
    const identitySet = new Set(groupBy);
    aggFields = fields
      .filter((f) => !identitySet.has(f))
      .map((f) => ({ field: f, fn: 'values' as AggFn }));
  }

  return {
    fields,
    groupBy,
    aggFields,
    timeBucket: spec.timeBucket,
    conditionStatement: spec.conditionStatement,
  };
}
